import { BLOCK_CODE, BLOCK_CODE_LINE, MARK_CODE } from '@gitbook/doc-core';
import AutoReplace from '@gitbook/slate-auto-replace';

/**
 * Typing '--' and 'space' inserts an em-dash. See plugin below for backspace handling.
 */
const emDash = AutoReplace({
    onInput: ' ',
    before: /--$/,
    ignoreIn: [MARK_CODE, BLOCK_CODE, BLOCK_CODE_LINE],
    transform: (change) => {
        return change.deleteBackward(2).insertText('— ');
    },
    revertOnBackspace: {
        from: /— $/,
        to: '-- ',
    },
});

/**
 * Convert common fractions to their single characters
 */
const fractions = AutoReplace({
    onInput: ' ',
    before: /(?:1\/2|1\/3|2\/3|1\/4|3\/4|1\/5|2\/5|3\/5|4\/5|1\/6|5\/6)$/,
    ignoreIn: [MARK_CODE, BLOCK_CODE, BLOCK_CODE_LINE],
    transform: (change, _e, matches) => {
        const fractionMap: Record<string, string> = {
            '1/2': '½',
            '1/3': '⅓',
            '2/3': '⅔',
            '1/4': '¼',
            '3/4': '¾',
            '1/5': '⅕',
            '2/5': '⅖',
            '3/5': '⅗',
            '4/5': '⅘',
            '1/6': '⅙',
            '5/6': '⅚',
        };
        const match = matches.before[0];
        return change.deleteBackward(match.length).insertText(`${fractionMap[match]} `);
    },
    revertOnBackspace: {
        from: /[½¼¾⅓⅔⅕⅖⅗⅘⅙⅚]\s$/,
        to: (match: string): string => {
            const reverseMap: Record<string, string> = {
                '½': '1/2 ',
                '⅓': '1/3 ',
                '⅔': '2/3 ',
                '¼': '1/4 ',
                '¾': '3/4 ',
                '⅕': '1/5 ',
                '⅖': '2/5 ',
                '⅗': '3/5 ',
                '⅘': '4/5 ',
                '⅙': '1/6 ',
                '⅚': '5/6 ',
            };
            return reverseMap[match] || match;
        },
    },
});

/**
 * Typing '!=' and 'space' inserts not equal symbol
 */
const notEqual = AutoReplace({
    onInput: ' ',
    before: /!=$/,
    ignoreIn: [MARK_CODE, BLOCK_CODE, BLOCK_CODE_LINE],
    transform: (change) => {
        return change.deleteBackward(2).insertText('≠ ');
    },
    revertOnBackspace: {
        from: /≠$/,
        to: '!= ',
    },
});

/**
 * Typing '>=' and 'space' inserts greater than or equal symbol
 */
const greaterEqual = AutoReplace({
    onInput: ' ',
    before: />=$/,
    ignoreIn: [MARK_CODE, BLOCK_CODE, BLOCK_CODE_LINE],
    transform: (change) => {
        return change.deleteBackward(2).insertText('≥ ');
    },
    revertOnBackspace: {
        from: /≥$/,
        to: '>= ',
    },
});

/**
 * Typing '=>' and 'space' inserts double right arrow
 */
const doubleRightArrow = AutoReplace({
    onInput: ' ',
    before: /=>$/,
    ignoreIn: [MARK_CODE, BLOCK_CODE, BLOCK_CODE_LINE],
    transform: (change) => {
        return change.deleteBackward(2).insertText('⇒ ');
    },
    revertOnBackspace: {
        from: /⇒$/,
        to: '=> ',
    },
});

export const plugins = [emDash, fractions, notEqual, greaterEqual, doubleRightArrow];
